import { useNavigate } from "react-router-dom";
import { Captions, Check, FolderInput, Loader2, Mic, MoreHorizontal, PencilLine, Trash2, Upload, X } from "lucide-react";

import { detectTextLang, translations } from "utils/i18n";
import {
  findProjectName,
  formatCreated,
  formatDuration,
  languageLabel,
  transcriptEditorPath,
  transcriptName,
} from "utils/transcript";
import { useLanguage } from "context/languageContext";
import { cn } from "lib/utils";
import { Button } from "components/ui/button";
import { Skeleton } from "components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "components/ui/dropdown-menu";

export const IN_FLIGHT_STATUSES = ["pending", "processing"];

// One grid definition shared by the header row and every data row, so the
// columns can never drift apart. Mirrors arkii-product's file-list layout —
// the same list (and the same columns) backs Home and a project page alike.
export const ROW_GRID = [
  "grid items-center gap-x-3",
  "grid-cols-[auto_minmax(0,1fr)_auto]",
  "sm:grid-cols-[auto_minmax(0,1fr)_4.5rem_4.5rem_auto]",
  "md:grid-cols-[auto_minmax(0,1fr)_4.5rem_4.5rem_6rem_auto]",
  "lg:grid-cols-[auto_minmax(0,1fr)_4.5rem_4.5rem_6rem_7rem_7rem_auto]",
].join(" ");

export const META_MD = "hidden md:block";
export const META_LG = "hidden lg:block";
export const META_SM = "hidden sm:block";

const SOURCE_ICON = { recorded: Mic, uploaded: Upload, caption: Captions };

// `tr` defaults to English so the one external caller that doesn't pass a
// language-aware `tr` (ImportTranscriptionDialog) keeps working unchanged.
export function SourceIcon({ source, tr = translations.en.transcriptionList }) {
  const Icon = SOURCE_ICON[source] ?? Upload;
  const SOURCE_LABEL = { recorded: tr.sourceRecorded, uploaded: tr.sourceUploaded, caption: tr.sourceCaption };
  const label = SOURCE_LABEL[source] ?? tr.sourceUploaded;
  return (
    <span
      title={label}
      className="flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground"
    >
      <Icon className="size-4" aria-hidden="true" />
      <span className="sr-only">{label}</span>
    </span>
  );
}

export function StatusBadge({ status, tr }) {
  if (status === "failed")
    return <span className="text-xs font-medium text-destructive">{tr.statusFailed}</span>;
  if (IN_FLIGHT_STATUSES.includes(status))
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground">
        <Loader2 className="size-3 animate-spin" />
        {tr.statusProcessing}
      </span>
    );
  return <span className="text-xs font-medium text-emerald-500">{tr.statusDone}</span>;
}

function uploadStatusLabel(upload, tr) {
  if (upload.phase === "creating") return tr.phasePreparing;
  if (upload.phase === "uploading") return tr.phaseUploadingPercent(upload.progress);
  if (upload.phase === "validating") return tr.phaseValidating;
  if (upload.phase === "cancelling") return tr.phaseCancelling;
  return tr.phaseUploading;
}

export function UploadStatusBadge({ upload, tr }) {
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground">
      <Loader2 className="size-3 animate-spin" />
      {uploadStatusLabel(upload, tr)}
    </span>
  );
}

function MobileMetaItem({ label, children }) {
  return (
    <span className="min-w-0 text-xs text-muted-foreground">
      <span className="me-1 text-[11px] text-muted-foreground/70">{label}:</span>
      <span className="inline-block max-w-full truncate align-bottom text-foreground">{children}</span>
    </span>
  );
}

function MobileMeta({ children }) {
  return <div className="mobile-row-meta col-span-3 grid min-w-0 grid-cols-2 gap-x-3 gap-y-1 pt-1 sm:hidden">{children}</div>;
}

function ActiveUploadRow({ upload, projectName, tr, language }) {
  return (
    <li className={cn(ROW_GRID, "relative rounded-lg px-3 py-2")}>
      <SourceIcon source={upload.source} tr={tr} />

      <div className="min-w-0">
        <span lang={detectTextLang(upload.filename)} className="block truncate text-sm font-medium leading-6 text-foreground">
          {upload.filename}
        </span>
      </div>

      <span className={cn(META_SM, "font-mono text-xs text-muted-foreground")}>—</span>
      <span className={cn(META_SM, "font-mono text-xs text-muted-foreground")}>—</span>

      <span className={META_MD}>
        <UploadStatusBadge upload={upload} tr={tr} />
      </span>

      <span className={cn(META_LG, "truncate text-xs text-muted-foreground")}>{projectName ?? "—"}</span>
      <span className={cn(META_LG, "truncate text-xs text-muted-foreground")}>
        {formatCreated(upload.startedAt, language)}
      </span>

      {upload.busy ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-8 text-muted-foreground sm:size-7"
          onClick={upload.cancel}
          disabled={upload.phase === "cancelling"}
          title={tr.cancelUpload}
        >
          <X className="size-4" />
          <span className="sr-only">{tr.cancelUpload}</span>
        </Button>
      ) : (
        <span className="size-7" aria-hidden="true" />
      )}

      <MobileMeta>
        <MobileMetaItem label={tr.colLanguage}>—</MobileMetaItem>
        <MobileMetaItem label={tr.colDuration}>—</MobileMetaItem>
        <MobileMetaItem label={tr.colStatus}>
          <UploadStatusBadge upload={upload} tr={tr} />
        </MobileMetaItem>
        <MobileMetaItem label={tr.colProject}>{projectName ?? "—"}</MobileMetaItem>
        <MobileMetaItem label={tr.colCreated}>{formatCreated(upload.startedAt, language)}</MobileMetaItem>
      </MobileMeta>
    </li>
  );
}

function TranscriptionRow({ item, projects, onRename, onDelete, onMoveToProject, tr, language }) {
  const navigate = useNavigate();
  const projectName = findProjectName(projects, item.project);
  const name = transcriptName(item, language);

  return (
    <li
      onClick={() => navigate(transcriptEditorPath(item))}
      className={cn(
        ROW_GRID,
        "group relative cursor-pointer rounded-lg px-3 py-2 transition-colors hover:bg-muted focus-within:bg-muted"
      )}
    >
      <SourceIcon source={item.source} tr={tr} />

      <div className="min-w-0">
        <span lang={detectTextLang(name)} className="block truncate text-sm font-medium leading-6 text-foreground">
          {name}
        </span>
      </div>

      <span className={cn(META_SM, "font-mono text-xs text-muted-foreground")}>{languageLabel(item)}</span>

      <span className={cn(META_SM, "font-mono text-xs tabular-nums text-muted-foreground")}>
        {formatDuration(item.duration_seconds)}
      </span>

      <span className={META_MD}>
        <StatusBadge status={item.status} tr={tr} />
      </span>

      <span className={cn(META_LG, "truncate text-xs text-muted-foreground")}>{projectName ?? "—"}</span>

      <span className={cn(META_LG, "truncate text-xs text-muted-foreground")}>
        {formatCreated(item.created_at, language)}
      </span>

      {/* DropdownMenuContent renders in a portal, but it's still a React
          child of this div, so stopping propagation here also catches
          clicks on the portaled menu items — not just the trigger. */}
      <div onClick={(event) => event.stopPropagation()}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="relative z-10 size-8 text-muted-foreground sm:size-7">
              <MoreHorizontal className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuItem onClick={() => onRename(item)}>
              <PencilLine className="size-4" />
              {tr.rename}
            </DropdownMenuItem>

            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <FolderInput className="size-4 rtl:-scale-x-100" />
                {tr.moveToProject}
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <DropdownMenuItem onClick={() => onMoveToProject(item, null)}>
                  {!item.project ? <Check className="size-4" /> : <span className="size-4" aria-hidden="true" />}
                  {tr.noProject}
                </DropdownMenuItem>
                {projects.length > 0 ? <DropdownMenuSeparator /> : null}
                {projects.map((project) => (
                  <DropdownMenuItem key={project.id} onClick={() => onMoveToProject(item, project.id)}>
                    {item.project === project.id ? (
                      <Check className="size-4" />
                    ) : (
                      <span className="size-4" aria-hidden="true" />
                    )}
                    <span className="truncate" lang={detectTextLang(project.name)}>
                      {project.name}
                    </span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>

            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => onDelete(item)}>
              <Trash2 className="size-4" />
              {tr.delete}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <MobileMeta>
        <MobileMetaItem label={tr.colLanguage}>{languageLabel(item)}</MobileMetaItem>
        <MobileMetaItem label={tr.colDuration}>{formatDuration(item.duration_seconds)}</MobileMetaItem>
        <MobileMetaItem label={tr.colStatus}>
          <StatusBadge status={item.status} tr={tr} />
        </MobileMetaItem>
        <MobileMetaItem label={tr.colProject}>{projectName ?? "—"}</MobileMetaItem>
        <MobileMetaItem label={tr.colCreated}>{formatCreated(item.created_at, language)}</MobileMetaItem>
      </MobileMeta>
    </li>
  );
}

/** The one header row, shared by the populated list, the empty scaffold, and the loading skeleton below. */
function TranscriptionListHeader({ tr }) {
  return (
    <div className={cn("transcription-list-header", ROW_GRID, "px-3 pb-2 text-xs font-medium text-muted-foreground")}>
      <span className="size-8" aria-hidden="true" />
      <span className="text-start">{tr.colName}</span>
      <span className={cn(META_SM, "text-start")}>{tr.colLanguage}</span>
      <span className={cn(META_SM, "text-start")}>{tr.colDuration}</span>
      <span className={cn(META_MD, "text-start")}>{tr.colStatus}</span>
      <span className={cn(META_LG, "text-start")}>{tr.colProject}</span>
      <span className={cn(META_LG, "text-start")}>{tr.colCreated}</span>
      <span className="size-7" aria-hidden="true" />
    </div>
  );
}

// Sample rows for the empty-state scaffold below — shaped like real
// transcriptions (not abstract bars) so the table reads as "this is what
// your files will look like," just faded out. `minutesAgo` is resolved to a
// timestamp at render time so "Created" always shows something plausible
// (Today/Yesterday/a recent date) no matter when this is viewed.
const PLACEHOLDER_ITEMS = [
  { source: "uploaded", name: "Weekly Team Sync", language_detected: "en", duration_seconds: 754, status: "completed", minutesAgo: 40 },
  { source: "recorded", name: "Client Onboarding Call", language_detected: "ar", duration_seconds: 1220, status: "completed", minutesAgo: 260 },
  { source: "caption", name: "Product Launch Walkthrough", language_detected: "en", duration_seconds: 145, status: "completed", minutesAgo: 1500 },
  { source: "uploaded", name: "Podcast Episode 42", language_detected: "en", duration_seconds: 2734, status: "processing", minutesAgo: 6 },
  { source: "recorded", name: "Interview — Sarah K.", language_detected: "ar", duration_seconds: 890, status: "completed", minutesAgo: 2950 },
  { source: "caption", name: "Tutorial: Getting Started", language_detected: "en", duration_seconds: 320, status: "completed", minutesAgo: 4200 },
];

// A static (non-animated, non-interactive) stand-in for a real row, built
// from the same sub-pieces as TranscriptionRow. Used only to scaffold the
// table when it's genuinely empty, so the columns stay visible and look like
// what real data will look like, instead of collapsing into a blank box.
function PlaceholderRow({ item, tr, language }) {
  const createdAt = new Date(Date.now() - item.minutesAgo * 60000).toISOString();

  return (
    <li className={cn(ROW_GRID, "rounded-lg px-3 py-2")} aria-hidden="true">
      <SourceIcon source={item.source} tr={tr} />

      <div className="min-w-0">
        <span lang={detectTextLang(item.name)} className="block truncate text-sm font-medium leading-6 text-foreground">
          {item.name}
        </span>
      </div>

      <span className={cn(META_SM, "font-mono text-xs text-muted-foreground")}>{languageLabel(item)}</span>
      <span className={cn(META_SM, "font-mono text-xs tabular-nums text-muted-foreground")}>
        {formatDuration(item.duration_seconds)}
      </span>

      <span className={META_MD}>
        <StatusBadge status={item.status} tr={tr} />
      </span>

      <span className={cn(META_LG, "truncate text-xs text-muted-foreground")}>—</span>
      <span className={cn(META_LG, "truncate text-xs text-muted-foreground")}>
        {formatCreated(createdAt, language)}
      </span>

      <span className="size-7" />

      <MobileMeta>
        <MobileMetaItem label={tr.colLanguage}>{languageLabel(item)}</MobileMetaItem>
        <MobileMetaItem label={tr.colDuration}>{formatDuration(item.duration_seconds)}</MobileMetaItem>
        <MobileMetaItem label={tr.colStatus}>
          <StatusBadge status={item.status} tr={tr} />
        </MobileMetaItem>
        <MobileMetaItem label={tr.colProject}>—</MobileMetaItem>
        <MobileMetaItem label={tr.colCreated}>{formatCreated(createdAt, language)}</MobileMetaItem>
      </MobileMeta>
    </li>
  );
}

/**
 * The one row list used by Home and a project page, so the columns — and the
 * row actions — can never drift apart between them. Ported from
 * arkii-product's FileList/FileRow split.
 */
export function TranscriptionList({
  items,
  projects,
  activeUpload,
  activeUploadProjectName,
  onRename,
  onDelete,
  onMoveToProject,
}) {
  const { language } = useLanguage();
  const tr = translations[language].transcriptionList;

  if (items.length === 0 && !activeUpload?.visible) {
    return (
      <div className="transcription-list">
        <TranscriptionListHeader tr={tr} />

        <ul className="border-t border-border pt-1 opacity-40">
          {PLACEHOLDER_ITEMS.map((item, index) => (
            <PlaceholderRow key={index} item={item} tr={tr} language={language} />
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div className="transcription-list">
      <TranscriptionListHeader tr={tr} />

      <ul className="border-t border-border pt-1">
        {activeUpload?.visible && (
          <ActiveUploadRow upload={activeUpload} projectName={activeUploadProjectName} tr={tr} language={language} />
        )}
        {items.map((item) => (
          <TranscriptionRow
            key={item.id}
            item={item}
            projects={projects}
            onRename={onRename}
            onDelete={onDelete}
            onMoveToProject={onMoveToProject}
            tr={tr}
            language={language}
          />
        ))}
      </ul>
    </div>
  );
}

/** Loading placeholder shaped exactly like TranscriptionList, for the first fetch. */
export function TranscriptionListSkeleton({ rows = 6 }) {
  const { language } = useLanguage();
  const tr = translations[language].transcriptionList;

  return (
    <div className="transcription-list">
      <TranscriptionListHeader tr={tr} />

      <ul className="border-t border-border pt-1">
        {Array.from({ length: rows }).map((_, index) => (
          <li key={index} className={cn(ROW_GRID, "px-3 py-2")}>
            <Skeleton className="size-8 rounded-md" />
            <Skeleton className="h-4 w-2/3 max-w-56" />
            <Skeleton className={cn(META_SM, "h-4 w-10")} />
            <Skeleton className={cn(META_SM, "h-4 w-10")} />
            <Skeleton className={cn(META_MD, "h-5 w-16 rounded-full")} />
            <Skeleton className={cn(META_LG, "h-4 w-14")} />
            <Skeleton className={cn(META_LG, "h-4 w-16")} />
            <Skeleton className="size-7 rounded-md" />
          </li>
        ))}
      </ul>
    </div>
  );
}
